import { createContext, useContext, useState, useCallback, useMemo, useEffect } from "react";
import { api } from "@/lib/api";

const dict = {
    es: {
        nav: {
            markets: "Mercados",
            live: "En vivo",
            rankings: "Rankings",
            creators: "Creadores",
            login: "Iniciar sesión",
            register: "Regístrate",
            wallet: "Wallet",
            profile: "Perfil",
            logout: "Cerrar sesión",
            admin: "Admin",
        },
        home: {
            hero_eyebrow: "Predicciones sociales basadas en habilidad",
            hero_title_a: "Predice en lo que",
            hero_title_b: "mueve el mundo",
            hero_subtitle: "Compite contra otros prediciendo deportes, economía, política y cultura. No es suerte, es habilidad.",
            cta_primary: "Empieza a jugar",
            cta_secondary: "Ver mercados",
            claim: "Juega. Acierta. Gana.",
            stat_users: "Jugadores",
            stat_markets: "Mercados",
            stat_pool: "Puntos en juego",
            stat_live: "En vivo",
            live_ticker_title: "EN VIVO AHORA",
            categories_title: "Explora por categoría",
            categories_sub: "Elige tu cancha. Cada categoría son cientos de mercados.",
            live_title: "En vivo ahora",
            live_sub: "Mercados con actualizaciones al minuto.",
            popular_title: "Mercados populares",
            popular_sub: "Los más predichos esta semana.",
            why_title: "Por qué Play2Win",
            why_1_title: "Sin casa",
            why_1_desc: "Compites contra otros, no contra la plataforma. Solo cobramos comisión del bote.",
            why_2_title: "Habilidad sobre azar",
            why_2_desc: "Gana quien mejor analiza. Tu track record sube tu nivel y tu reputación.",
            why_3_title: "Transparente",
            why_3_desc: "Cada mercado declara su fuente antes de abrirse. Resoluciones auditables.",
            why_4_title: "Solo puntos virtuales",
            why_4_desc: "MVP sin dinero real. Aprende, compite y sube en el ranking.",
            view_all: "Ver todos",
        },
        market: {
            pool: "Bote total",
            closes_in: "Cierra en",
            participants: "participantes",
            live: "EN VIVO",
            place_prediction: "Predecir",
            choose_option: "Elige una opción",
            points: "puntos",
            potential_return: "Retorno potencial",
            confirm: "Confirmar predicción",
            login_required: "Inicia sesión para predecir",
            success_placed: "¡Predicción registrada!",
            resolution_source: "Fuente de resolución",
            balance: "Tu saldo",
            stake: "Cantidad",
            featured: "Destacado",
        },
        footer: {
            terms: "Términos",
            responsible: "Juego responsable",
            privacy: "Privacidad",
            help: "Ayuda",
            tagline: "Convertimos tu conocimiento en un juego social donde gana quien mejor predice.",
        },
        auth: {
            login_title: "Bienvenido de vuelta",
            login_sub: "Entra y sigue ganando puntos",
            register_title: "Crea tu cuenta",
            register_sub: "Te regalamos 1.000 puntos al registrarte",
            email: "Email",
            password: "Contraseña",
            username: "Nombre de usuario",
            login_cta: "Entrar",
            register_cta: "Crear cuenta",
            have_account: "¿Ya tienes cuenta?",
            no_account: "¿No tienes cuenta?",
        },
        rankings: {
            title: "Rankings",
            sub: "Los mejores predictores. Sube de nivel ganando.",
            rank: "Pos",
            user: "Jugador",
            level: "Nivel",
            points: "Puntos",
            win_rate: "Aciertos",
            predictions: "Predicciones",
        },
        wallet: {
            title: "Tu wallet",
            balance: "Saldo de puntos",
            history: "Historial",
            my_preds: "Mis predicciones",
            type: "Tipo",
            amount: "Cantidad",
            date: "Fecha",
            status: "Estado",
            market: "Mercado",
            option: "Opción",
            stake: "Apostado",
            payout: "Premio",
            empty: "Aún no hay movimientos. ¡Haz tu primera predicción!",
        },
        creators: {
            title: "Conviértete en Creador",
            sub: "Diseña tus propios mercados, construye audiencia y gana comisión de cada bote.",
            cta: "Solicitar acceso",
            stat_volume: "Volumen creador",
            stat_payout: "Comisión por mercado",
        },
    },
    en: {
        nav: {
            markets: "Markets",
            live: "Live",
            rankings: "Rankings",
            creators: "Creators",
            login: "Sign in",
            register: "Sign up",
            wallet: "Wallet",
            profile: "Profile",
            logout: "Log out",
            admin: "Admin",
        },
        home: {
            hero_eyebrow: "Skill-based social predictions",
            hero_title_a: "Predict what",
            hero_title_b: "moves the world",
            hero_subtitle: "Compete with others predicting sports, economy, politics and culture. It's not luck, it's skill.",
            cta_primary: "Start playing",
            cta_secondary: "Explore markets",
            claim: "Play. Predict. Win.",
            stat_users: "Players",
            stat_markets: "Markets",
            stat_pool: "Points in play",
            stat_live: "Live",
            live_ticker_title: "LIVE NOW",
            categories_title: "Explore by category",
            categories_sub: "Pick your court. Each category has hundreds of markets.",
            live_title: "Live now",
            live_sub: "Markets updating every minute.",
            popular_title: "Popular markets",
            popular_sub: "Most predicted this week.",
            why_title: "Why Play2Win",
            why_1_title: "No house",
            why_1_desc: "You compete vs other players. We just take a small commission on the pool.",
            why_2_title: "Skill over luck",
            why_2_desc: "Best analyst wins. Your track record builds your level and reputation.",
            why_3_title: "Transparent",
            why_3_desc: "Every market discloses its source before opening. Auditable resolutions.",
            why_4_title: "Virtual points only",
            why_4_desc: "MVP with no real money. Learn, compete, climb the leaderboard.",
            view_all: "View all",
        },
        market: {
            pool: "Total pool",
            closes_in: "Closes in",
            participants: "players",
            live: "LIVE",
            place_prediction: "Predict",
            choose_option: "Pick an option",
            points: "points",
            potential_return: "Potential return",
            confirm: "Confirm prediction",
            login_required: "Sign in to predict",
            success_placed: "Prediction placed!",
            resolution_source: "Resolution source",
            balance: "Your balance",
            stake: "Stake",
            featured: "Featured",
        },
        footer: {
            terms: "Terms",
            responsible: "Responsible play",
            privacy: "Privacy",
            help: "Help",
            tagline: "We turn your knowledge into a social game where the best predictor wins.",
        },
        auth: {
            login_title: "Welcome back",
            login_sub: "Log in and keep stacking points",
            register_title: "Create your account",
            register_sub: "We gift you 1,000 points on signup",
            email: "Email",
            password: "Password",
            username: "Username",
            login_cta: "Sign in",
            register_cta: "Create account",
            have_account: "Have an account?",
            no_account: "Don't have an account?",
        },
        rankings: {
            title: "Rankings",
            sub: "Top predictors. Level up by winning.",
            rank: "Rk",
            user: "Player",
            level: "Level",
            points: "Points",
            win_rate: "Win rate",
            predictions: "Predictions",
        },
        wallet: {
            title: "Your wallet",
            balance: "Points balance",
            history: "History",
            my_preds: "My predictions",
            type: "Type",
            amount: "Amount",
            date: "Date",
            status: "Status",
            market: "Market",
            option: "Option",
            stake: "Stake",
            payout: "Payout",
            empty: "No movements yet. Make your first prediction!",
        },
        creators: {
            title: "Become a Creator",
            sub: "Design your own markets, build audience, earn commission from every pool.",
            cta: "Request access",
            stat_volume: "Creator volume",
            stat_payout: "Commission per market",
        },
    },
};

const I18nContext = createContext(null);

// Detect preview mode from URL (?preview=1). Used to fetch draft content.
function isPreviewMode() {
    try {
        return new URLSearchParams(window.location.search).get("preview") === "1";
    } catch {
        return false;
    }
}

export function I18nProvider({ children }) {
    const [lang, setLang] = useState(() => {
        try {
            return localStorage.getItem("p2w_lang") || "es";
        } catch {
            return "es";
        }
    });

    // CMS overrides loaded from /api/content. Map: { "home.hero_title_a": { es, en, type } }
    const [overrides, setOverrides] = useState({});
    const [previewMode, setPreviewMode] = useState(() => isPreviewMode());

    const reloadContent = useCallback(async () => {
        try {
            const preview = isPreviewMode();
            const { data } = await api.get(`/content${preview ? "?preview=1" : ""}`);
            setOverrides(data?.items || {});
            setPreviewMode(!!data?.preview);
        } catch {
            // CMS is optional; on failure we just use the hardcoded dict
        }
    }, []);

    useEffect(() => {
        reloadContent();
    }, [reloadContent]);

    const setLanguage = useCallback((l) => {
        setLang(l);
        try {
            localStorage.setItem("p2w_lang", l);
        } catch {
            /* ignore */
        }
    }, []);

    const t = useCallback(
        (path) => {
            // 1) CMS override (DB-backed, language-aware) takes priority
            const ov = overrides[path];
            if (ov) {
                const val = lang === "en" ? ov.en : ov.es;
                if (val !== undefined && val !== null && val !== "") return val;
            }
            // 2) Fallback to hardcoded dict (legacy + UI strings not yet in CMS)
            const parts = path.split(".");
            let v = dict[lang];
            for (const p of parts) {
                if (v && typeof v === "object" && p in v) v = v[p];
                else return path;
            }
            return v;
        },
        [lang, overrides],
    );

    const value = useMemo(
        () => ({ lang, setLanguage, t, reloadContent, previewMode, overrides }),
        [lang, setLanguage, t, reloadContent, previewMode, overrides],
    );
    return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
    return useContext(I18nContext);
}
